import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles add confirmation, sticky summary layout, and the
 * installments CTA close-on-click. Native cart events retain count updates.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const fromDrawer = event.target instanceof Element && Boolean(event.target.closest('#cart-drawer'));
    event.promise
      ?.then(({ detail }) => {
        requestAnimationFrame(() => this.#updateStickyState());
        // ATW: every add to cart that happens outside the drawer shows the «Προστέθηκε στο καλάθι» bar
        // (with a «ΔΕΣ ΤΟ ΚΑΛΑΘΙ» button that opens the drawer). Adds made inside the drawer (recommendations) show nothing.
        if (event.action === 'add' && !detail?.didError && !fromDrawer && !this.#themeDrawer?.hasAttribute('open')) {
          const bar = document.querySelector('atw-cart-added-confirmation');
          // ATW: show WHAT was added (image, name, size, price). The just-added variant comes from the event;
          // the cart lines come from the add response, or from /cart.js when the source did not provide them.
          const line = event.lines?.[0];
          const variantId = String(line?.merchandiseId ?? '').replace(/\D/g, '');
          const pick = (items) =>
            Array.isArray(items) && items.length
              ? (variantId && items.find((i) => String(i.variant_id) === variantId)) || (variantId ? null : items[0])
              : null;
          const item = pick(detail?.items);
          requestAnimationFrame(() => bar?.show(item, line?.quantity));
          if (!item && bar?.dataset.showItem === 'true') {
            fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
              .then((r) => (r.ok ? r.json() : null))
              .then((cart) => bar.setItem(pick(cart?.items), line?.quantity))
              .catch(() => {});
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

/** MODULE: ATW — Cart — Added Confirmation. Bar shown after every add to cart outside the drawer; its button opens the drawer.
 *  While the search modal (a top-layer <dialog>) is open, the bar is moved inside it so it is not hidden behind it,
 *  and put back where it was when it closes. */
class ATWCartAddedConfirmation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.querySelector('[data-atw-dismiss]')?.addEventListener('click', () => this.hide(), {signal});
    this.querySelector('[data-atw-open-cart]')?.addEventListener('click', () => {
      this.hide();
      document.querySelector('theme-drawer#cart-drawer')?.open();
    }, {signal});
    // Keep the bar while the shopper touches it, hovers it or focuses a button inside it; restart the countdown afterwards.
    ['pointerenter', 'pointerdown', 'focusin'].forEach((type) => this.addEventListener(type, () => clearTimeout(this.timer), {signal}));
    ['pointerleave', 'pointerup', 'pointercancel', 'focusout'].forEach((type) => this.addEventListener(type, () => this.#arm(), {signal}));
  }
  disconnectedCallback() { this.controller?.abort(); clearTimeout(this.timer); }
  get #duration() {
    const seconds = Number(this.dataset.duration);
    return Math.min(30, Math.max(3, Number.isFinite(seconds) && seconds > 0 ? seconds : 9)) * 1000;
  }
  #arm() {
    clearTimeout(this.timer);
    if (this.hidden) return;
    this.timer = setTimeout(() => this.hide(), this.#duration);
  }
  /** Fills the optional «what was added» line. All text goes through textContent. */
  setItem(item, addedQuantity) {
    const thumb = this.querySelector('.atw-cart-added__thumb');
    const text = this.querySelector('.atw-cart-added__item');
    if (!thumb || !text) return;
    if (!item || this.dataset.showItem !== 'true') {
      thumb.hidden = true; text.hidden = true; text.textContent = '';
      return;
    }
    let name = String(item.product_title || item.title || '').trim();
    if (this.dataset.cleanTitle !== 'false') name = name.split(/\s+\|\s+/)[0].trim() || name;
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? String(item.variant_title) : '';
    const qty = Number(addedQuantity) > 1 ? '× ' + Number(addedQuantity) : '';
    let price = '';
    try {
      const unit = item.final_price ?? item.price;
      if (typeof unit === 'number' && this.dataset.moneyFormat) price = formatMoney(unit, this.dataset.moneyFormat, this.dataset.currency || 'EUR');
    } catch (e) { price = ''; }
    text.textContent = [name, variant, qty, price].filter(Boolean).join(' · ');
    text.hidden = !text.textContent;
    const src = item.image || item.featured_image?.url;
    if (src) {
      thumb.src = src + (src.includes('?') ? '&' : '?') + 'width=96';
      thumb.hidden = false;
    } else {
      thumb.hidden = true;
    }
  }
  #place() {
    const host = document.querySelector('.search-modal__content[open]');
    if (host && this.parentElement !== host) {
      if (!this._home) this._home = { parent: this.parentNode, next: this.nextSibling };
      host.appendChild(this);
    }
    this.classList.toggle('atw-cart-added--in-search', Boolean(host));
  }
  #restore() {
    this.classList.remove('atw-cart-added--in-search');
    const home = this._home;
    if (!home) return;
    this._home = null;
    if (this.parentNode === home.parent) return;
    const parent = home.parent?.isConnected ? home.parent : document.body;
    const next = home.next && home.next.parentNode === parent ? home.next : null;
    parent.insertBefore(this, next);
  }
  show(item, addedQuantity) {
    clearTimeout(this.timer);
    this.#place();
    this.setItem(item, addedQuantity);
    this.hidden = false;
    this.#arm();
  }
  hide() { this.hidden = true; clearTimeout(this.timer); this.#restore(); }
}
if (!customElements.get('atw-cart-added-confirmation')) {
  customElements.define('atw-cart-added-confirmation', ATWCartAddedConfirmation);
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
